/** Pure sort and distance for the ranking page. */

export function milesBetween(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 3958.7613 * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function withMiles(schools, home) {
  const lat = home?.lat;
  const lon = home?.lon;
  const hasHome = Number.isFinite(lat) && Number.isFinite(lon);
  return schools.map((school) => {
    const canMeasure =
      hasHome && Number.isFinite(school.lat) && Number.isFinite(school.lon);
    return {
      ...school,
      miles: canMeasure ? milesBetween(lat, lon, school.lat, school.lon) : null,
    };
  });
}

function missing(value) {
  return value == null || value === "";
}

function compareValues(a, b, dir) {
  const aMissing = missing(a);
  const bMissing = missing(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === "string" || typeof b === "string") {
    return dir * String(a).localeCompare(String(b), "en", { numeric: true });
  }
  return dir * (a - b);
}

function rankBy(schools, key) {
  const ranked = schools
    .filter((school) => !missing(school[key]))
    .sort((a, b) => {
      const byValue = compareValues(a[key], b[key], -1);
      if (byValue !== 0) return byValue;
      return a.name.localeCompare(b.name, "en");
    });
  const places = new Map();
  ranked.forEach((school, index) => places.set(school.id, index + 1));
  return places;
}

/** GCSE place by Attainment 8, A level place by points. A missing figure has no rank. */
export function withStageRanks(schools) {
  const gcse = rankBy(schools, "attainment8");
  const alevel = rankBy(schools, "alevelPoints");
  return schools.map((school) => ({
    ...school,
    gcseRank: gcse.get(school.id) ?? null,
    alevelRank: alevel.get(school.id) ?? null,
  }));
}

function chosenKeys(value) {
  if (Array.isArray(value)) return value.filter((item) => item && item !== "All");
  if (value && value !== "All") return [value];
  return [];
}

export function rankSchools(schools, { gender, councils, catchment, query, sortKey, sortDir }) {
  const q = String(query || "").trim().toLowerCase();
  const chosen = Array.isArray(councils) ? councils.filter(Boolean) : [];
  const genders = chosenKeys(gender);
  const catchments = chosenKeys(catchment);
  const filtered = schools.filter((school) => {
    if (genders.length && !genders.includes(school.gender)) return false;
    if (chosen.length && !chosen.includes(school.area)) return false;
    if (catchments.length && !catchments.includes(school.catchmentKey)) return false;
    if (!q) return true;
    const hay = `${school.name} ${school.area || ""}`.toLowerCase();
    return hay.includes(q);
  });
  const sorted = [...filtered].sort((a, b) => {
    const byValue = compareValues(a[sortKey], b[sortKey], sortDir);
    if (byValue !== 0) return byValue;
    return a.name.localeCompare(b.name, "en");
  });
  return sorted.map((school, index) => ({ ...school, rank: index + 1 }));
}

export function formatRankValue(value, column) {
  if (missing(value)) return "—";
  if (column.text) return String(value);
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const digits = column.digits ?? 0;
  const text = n.toFixed(digits);
  if (column.signed && n > 0) return `+${text}`;
  if (column.suffix) return `${text}${column.suffix}`;
  return text;
}
